import React, { useState } from 'react';
import { X, Globe, CheckCircle, AlertCircle, Wifi, Zap, ShieldCheck } from 'lucide-react';
import { LocationData } from '../types';
import { TRANSIT_PROVIDERS, IXP_EXCHANGES, NAWAS_TIERS } from '../constants';

interface MmrModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLocation: string;
  locations: Record<string, LocationData>;
  asnPurchased: boolean;
  piIpPurchased: boolean;
  money: number;
  buildMmr: (locationId: string) => void;
  purchaseAsn: () => void;
  purchasePiIpSpace: () => void;
  orderTransit: (locationId: string, providerId: string, committedGbps: number) => void;
  cancelTransit: (locationId: string, providerId: string) => void;
  connectIxp: (locationId: string, ixpId: string, portSpeedGbps: number, asnOk: boolean, piIpOk: boolean) => void;
  disconnectIxp: (locationId: string, ixpId: string) => void;
  subscribeNawas: (locationId: string, tierId: string) => void;
  cancelNawas: (locationId: string) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
  onOpenNetworkModal?: () => void;
}

const MMR_BUILD_COST = 25000;
const ASN_COST = 500;
const PI_IP_COST = 200;

const MmrModal: React.FC<MmrModalProps> = ({
  isOpen, onClose, currentLocation, locations,
  asnPurchased, piIpPurchased, money,
  buildMmr, purchaseAsn, purchasePiIpSpace,
  orderTransit, cancelTransit,
  connectIxp, disconnectIxp,
  subscribeNawas, cancelNawas,
  formatCurrency,
  onOpenNetworkModal,
}) => {
  const [commitInputs, setCommitInputs] = useState<Record<string, string>>({});
  const [ixpPortSelections, setIxpPortSelections] = useState<Record<string, number>>({});

  if (!isOpen) return null;

  const location = locations[currentLocation];
  if (!location) return null;

  const mmr = location.mmr;
  const mmrBuilt = mmr?.built ?? false;
  const hasCoreSwitch = !!location.coreSwitch;
  const ixpReady = mmrBuilt && hasCoreSwitch && asnPurchased && piIpPurchased;

  const availableProviders = TRANSIT_PROVIDERS.filter(p => p.availableLocations.includes(currentLocation));
  const activeTransit = mmr?.transitProviders ?? [];
  const activeIxp = mmr?.ixpConnections ?? [];

  const totalTransitMonthlyCost = activeTransit.reduce((sum, tp) => {
    const p = TRANSIT_PROVIDERS.find(x => x.id === tp.providerId);
    return sum + (p ? p.costPerGbpsMonth * tp.committedGbps : 0);
  }, 0);
  const totalCommittedGbps = activeTransit.reduce((sum, tp) => sum + tp.committedGbps, 0);

  const totalIxpMonthlyCost = activeIxp.reduce((sum, c) => {
    const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
    const port = ixp?.ports.find(p => p.speedGbps === c.portSpeedGbps);
    return sum + (port?.monthlyCost ?? 0);
  }, 0);

  const totalIxpPortGbps = activeIxp.reduce((sum, c) => sum + c.portSpeedGbps, 0);

  // Peering ratio: IXP port capacity vs transit capacity → if >70% → 15% transit cost saving
  const peeringRatioOk = totalCommittedGbps > 0 && (totalIxpPortGbps / (totalIxpPortGbps + totalCommittedGbps)) >= 0.70;

  const availableIxps = IXP_EXCHANGES.filter(x => x.availableLocations.includes(currentLocation));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-cyan-400" />
            <h2 className="text-lg font-bold">Connectivity — {location.name}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">

          {/* ── Meet-Me Room ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Meet-Me Room (MMR)</h3>
            {mmrBuilt ? (
              <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg px-4 py-3 flex items-center gap-3">
                <CheckCircle size={16} className="text-emerald-400 shrink-0" />
                <span className="text-sm text-emerald-300">MMR operational — cross-connects available</span>
              </div>
            ) : (
              <div className="bg-gray-800 rounded-lg p-4 space-y-3">
                <p className="text-sm text-gray-300">
                  The Meet-Me Room is a dedicated space where carrier equipment co-locates with your network. Required to order transit providers and IXP connections.
                </p>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Construction cost: {formatCurrency(MMR_BUILD_COST)}</p>
                  </div>
                  <button
                    onClick={() => buildMmr(currentLocation)}
                    disabled={money < MMR_BUILD_COST}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Build MMR
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ── Global Network Identity ── */}
          <section>
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Network Identity (Global)</h3>
            <div className="space-y-2">
              <div className="bg-gray-800 rounded-lg px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Autonomous System Number (ASN)</p>
                  <p className="text-xs text-gray-400">Required for IXP peering. One-time: {formatCurrency(ASN_COST)}</p>
                </div>
                {asnPurchased ? (
                  <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle size={12} /> Owned</span>
                ) : (
                  <button onClick={purchaseAsn} disabled={money < ASN_COST}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed">
                    Purchase
                  </button>
                )}
              </div>
              <div className="bg-gray-800 rounded-lg px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Provider-Independent IP Space</p>
                  <p className="text-xs text-gray-400">Required for IXP peering. One-time: {formatCurrency(PI_IP_COST)}</p>
                </div>
                {piIpPurchased ? (
                  <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle size={12} /> Owned</span>
                ) : (
                  <button onClick={purchasePiIpSpace} disabled={money < PI_IP_COST}
                    className="px-3 py-1.5 rounded text-xs font-medium bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed">
                    Purchase
                  </button>
                )}
              </div>
              <div className={`rounded-lg px-4 py-2 text-xs ${ixpReady ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-300' : 'bg-gray-800/50 text-gray-500'}`}>
                {ixpReady ? '✓ IXP-ready: MMR + Core Switch + ASN + PI IP space all in place' : 'IXP peering requires: MMR + Core Switch + ASN + PI IP space'}
              </div>
            </div>
          </section>

          {/* ── IXP Peering ── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">IXP Peering</h3>
              {activeIxp.length > 0 && (
                <span className="text-xs text-gray-400">{totalIxpPortGbps} Gbps · {formatCurrency(totalIxpMonthlyCost)}/mo</span>
              )}
            </div>

            {!ixpReady ? (
              !hasCoreSwitch && mmrBuilt ? (
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-900/30 border border-amber-700 text-amber-300 text-sm">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={14} className="shrink-0" />
                    Install a Core Switch first — IXP ports terminate on it.
                  </div>
                  {onOpenNetworkModal && (
                    <button
                      onClick={() => { onClose(); onOpenNetworkModal(); }}
                      className="shrink-0 px-3 py-1.5 rounded bg-amber-700 hover:bg-amber-600 text-white text-xs font-semibold whitespace-nowrap"
                    >
                      Install Core Switch →
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {!mmrBuilt ? 'Build the MMR first.' : 'Purchase ASN and PI IP space to unlock IXP connections.'}
                </p>
              )
            ) : availableIxps.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No IXPs available at this location.</p>
            ) : (
              <div className="space-y-3">
                {availableIxps.map(ixp => {
                  const active = activeIxp.find(c => c.ixpId === ixp.id);
                  const selKey = `ixp-${currentLocation}-${ixp.id}`;
                  const selectedSpeed = ixpPortSelections[selKey] ?? ixp.ports[0].speedGbps;
                  const selectedPort = ixp.ports.find(p => p.speedGbps === selectedSpeed) ?? ixp.ports[0];

                  return (
                    <div key={ixp.id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Zap size={13} className="text-yellow-400" />
                            <span className="text-sm font-semibold">{ixp.name}</span>
                            <span title={`Connecting to ${ixp.name} adds +${Math.round(ixp.revenueBonus * 100)}% to server revenue at this location. Stacks up to +20% with multiple IXPs.`} className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-400 cursor-help">
                              +{Math.round(ixp.revenueBonus * 100)}% revenue ⓘ
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mb-2">{ixp.description}</p>
                        </div>

                        {active ? (
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-xs text-emerald-400 flex items-center gap-1">
                              <CheckCircle size={12} /> {active.portSpeedGbps}G port
                            </span>
                            <p className="text-xs text-gray-400">
                              {formatCurrency(ixp.ports.find(p => p.speedGbps === active.portSpeedGbps)?.monthlyCost ?? 0)}/mo
                            </p>
                            <button
                              onClick={() => disconnectIxp(currentLocation, ixp.id)}
                              className="text-xs px-2 py-1 rounded bg-red-900/50 hover:bg-red-800 text-red-300"
                            >
                              Disconnect
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <select
                              value={selectedSpeed}
                              onChange={e => setIxpPortSelections(prev => ({ ...prev, [selKey]: Number(e.target.value) }))}
                              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:border-cyan-500"
                            >
                              {ixp.ports.map(p => (
                                <option key={p.speedGbps} value={p.speedGbps}>{p.speedGbps}G</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-400">
                              Setup {formatCurrency(selectedPort.setupCost)} + {formatCurrency(selectedPort.monthlyCost)}/mo
                            </p>
                            <button
                              onClick={() => connectIxp(currentLocation, ixp.id, selectedSpeed, asnPurchased, piIpPurchased)}
                              disabled={money < selectedPort.setupCost + selectedPort.monthlyCost}
                              className="text-xs px-3 py-1.5 rounded bg-yellow-700 hover:bg-yellow-600 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                            >
                              Connect
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Peering ratio indicator */}
            {activeIxp.length > 0 && activeTransit.length > 0 && (
              <div className={`mt-3 rounded-lg px-4 py-2 text-xs flex items-center gap-2 ${peeringRatioOk ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-300' : 'bg-gray-800/50 text-gray-400'}`}>
                {peeringRatioOk
                  ? `✓ Peering ratio ${Math.round(totalIxpPortGbps / (totalIxpPortGbps + totalCommittedGbps) * 100)}% — transit costs reduced by 15%`
                  : `Peering ratio ${Math.round(totalIxpPortGbps / (totalIxpPortGbps + totalCommittedGbps) * 100)}% — reach 70% IXP share for a 15% transit cost discount`}
              </div>
            )}
          </section>

          {/* ── Transit Providers ── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Transit Providers</h3>
              {activeTransit.length > 0 && (
                <span className="text-xs text-gray-400">{totalCommittedGbps} Gbps · {formatCurrency(totalTransitMonthlyCost)}/mo</span>
              )}
            </div>

            {!mmrBuilt ? (
              <p className="text-sm text-gray-500 italic">Build the MMR to order transit providers.</p>
            ) : !hasCoreSwitch ? (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-amber-900/30 border border-amber-700 text-amber-300 text-sm">
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} className="shrink-0" />
                  Install a Core Switch first — transit cross-connects must terminate on it.
                </div>
                {onOpenNetworkModal && (
                  <button
                    onClick={() => { onClose(); onOpenNetworkModal(); }}
                    className="shrink-0 px-3 py-1.5 rounded bg-amber-700 hover:bg-amber-600 text-white text-xs font-semibold whitespace-nowrap"
                  >
                    Install Core Switch →
                  </button>
                )}
              </div>
            ) : availableProviders.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No transit providers available at this location.</p>
            ) : (
              <div className="space-y-3">
                {availableProviders.map(provider => {
                  const active = activeTransit.find(tp => tp.providerId === provider.id);
                  const inputKey = `${currentLocation}-${provider.id}`;
                  const inputVal = commitInputs[inputKey] ?? String(active?.committedGbps ?? provider.minCommitGbps);
                  const commitGbps = parseInt(inputVal, 10) || provider.minCommitGbps;
                  const validCommit = commitGbps >= provider.minCommitGbps && commitGbps <= provider.maxCommitGbps;
                  const previewCost = provider.costPerGbpsMonth * commitGbps;

                  return (
                    <div key={provider.id} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Wifi size={13} className={provider.color} />
                            <span className="text-sm font-semibold">{provider.name}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-700 text-gray-400">{provider.region}</span>
                          </div>
                          <p className="text-xs text-gray-400 mb-2">{provider.description}</p>
                          <p className="text-xs text-gray-500">
                            {formatCurrency(provider.costPerGbpsMonth)}/Gbps/mo · {provider.minCommitGbps}–{provider.maxCommitGbps} Gbps
                          </p>
                        </div>

                        {active ? (
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <span className="text-xs text-emerald-400 flex items-center gap-1">
                              <CheckCircle size={12} /> {active.committedGbps} Gbps active
                            </span>
                            <p className="text-xs text-gray-400">{formatCurrency(provider.costPerGbpsMonth * active.committedGbps)}/mo</p>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min={provider.minCommitGbps}
                                max={provider.maxCommitGbps}
                                value={inputVal}
                                onChange={e => setCommitInputs(prev => ({ ...prev, [inputKey]: e.target.value }))}
                                className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-cyan-500"
                              />
                              <span className="text-xs text-gray-400">Gbps</span>
                            </div>
                            {validCommit && commitGbps !== active.committedGbps && (
                              <p className="text-xs text-amber-400">{formatCurrency(provider.costPerGbpsMonth * commitGbps)}/mo new</p>
                            )}
                            <div className="flex items-center gap-1.5">
                              <button
                                disabled={!validCommit || commitGbps === active.committedGbps}
                                onClick={() => orderTransit(currentLocation, provider.id, commitGbps)}
                                className="text-xs px-2 py-1 rounded bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                              >
                                Adjust
                              </button>
                              <button
                                onClick={() => cancelTransit(currentLocation, provider.id)}
                                className="text-xs px-2 py-1 rounded bg-red-900/50 hover:bg-red-800 text-red-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-end gap-1.5 shrink-0">
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min={provider.minCommitGbps}
                                max={provider.maxCommitGbps}
                                value={inputVal}
                                onChange={e => setCommitInputs(prev => ({ ...prev, [inputKey]: e.target.value }))}
                                className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-cyan-500"
                              />
                              <span className="text-xs text-gray-400">Gbps</span>
                            </div>
                            {validCommit && (
                              <p className="text-xs text-amber-400 font-medium">
                                {formatCurrency(previewCost)}/mo ({formatCurrency(previewCost / 30)}/day recurring)
                              </p>
                            )}
                            <button
                              onClick={() => orderTransit(currentLocation, provider.id, commitGbps)}
                              disabled={!validCommit}
                              className="text-xs px-3 py-1.5 rounded bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
                            >
                              Order
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* ── Cost Summary ── */}
          {(activeTransit.length > 0 || activeIxp.length > 0) && (
            <section>
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Monthly Cost Summary</h3>
              <div className="bg-gray-800 rounded-lg divide-y divide-gray-700">
                {activeIxp.map(c => {
                  const ixp = IXP_EXCHANGES.find(x => x.id === c.ixpId);
                  const port = ixp?.ports.find(p => p.speedGbps === c.portSpeedGbps);
                  if (!ixp || !port) return null;
                  return (
                    <div key={c.ixpId} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className="text-yellow-400">{ixp.name}</span>
                      <span className="text-gray-300">{c.portSpeedGbps}G port</span>
                      <span className="text-red-400">−{formatCurrency(port.monthlyCost)}/mo</span>
                    </div>
                  );
                })}
                {activeTransit.map(tp => {
                  const p = TRANSIT_PROVIDERS.find(x => x.id === tp.providerId);
                  if (!p) return null;
                  const cost = p.costPerGbpsMonth * tp.committedGbps;
                  const discounted = peeringRatioOk ? cost * 0.85 : cost;
                  return (
                    <div key={tp.providerId} className="flex items-center justify-between px-4 py-2 text-sm">
                      <span className={p.color}>{p.name}</span>
                      <span className="text-gray-300">{tp.committedGbps} Gbps{peeringRatioOk ? ' (−15%)' : ''}</span>
                      <span className="text-red-400">−{formatCurrency(discounted)}/mo</span>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-4 py-2 text-sm font-semibold">
                  <span className="text-gray-300">Total</span>
                  <span className="text-gray-300" />
                  <span className="text-red-400">−{formatCurrency(totalIxpMonthlyCost + (peeringRatioOk ? totalTransitMonthlyCost * 0.85 : totalTransitMonthlyCost))}/mo</span>
                </div>
              </div>
            </section>
          )}

          {/* ── NaWas DDoS Protection ── */}
          {mmrBuilt && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} className="text-emerald-400" />
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">DDoS Protection (NaWas / NBIP)</h3>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                NBIP's Nationale Wasstraat reroutes attack traffic via BGP anycast to scrubbing centres, returning clean traffic via GRE tunnel. Requires ASN + PI IP space.
              </p>
              {!ixpReady ? (
                <p className="text-sm text-gray-500 italic">Requires ASN and PI IP space first.</p>
              ) : location.nawasSubscription ? (() => {
                const activeTier = NAWAS_TIERS.find(t => t.id === location.nawasSubscription!.tierId);
                return (
                  <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <ShieldCheck size={14} className="text-emerald-400" />
                          <span className="text-sm font-semibold text-emerald-300">{activeTier?.name} — Active</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {activeTier?.scrubCapacityGbps} Gbps scrubbing · {formatCurrency(activeTier?.monthlyCost ?? 0)}/mo
                        </p>
                        <p className="text-xs text-gray-500">DDoS attacks trigger BGP reroute — minimal revenue impact</p>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        {NAWAS_TIERS.filter(t => t.id !== location.nawasSubscription?.tierId && NAWAS_TIERS.indexOf(t) > NAWAS_TIERS.findIndex(x => x.id === location.nawasSubscription?.tierId)).slice(0, 1).map(upgrade => (
                          <button
                            key={upgrade.id}
                            onClick={() => subscribeNawas(currentLocation, upgrade.id)}
                            disabled={money < upgrade.setupCost}
                            className="px-3 py-1.5 rounded text-xs font-medium bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Upgrade → {upgrade.name} ({formatCurrency(upgrade.setupCost)} setup)
                          </button>
                        ))}
                        <button
                          onClick={() => cancelNawas(currentLocation)}
                          className="text-xs text-red-400 hover:text-red-300 underline"
                        >
                          Cancel subscription
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })() : (
                <div className="space-y-3">
                  {NAWAS_TIERS.map(tier => (
                    <div key={tier.id} className="bg-gray-800 rounded-lg p-4 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold">{tier.name}</p>
                        <p className="text-xs text-gray-400">{tier.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {tier.scrubCapacityGbps} Gbps scrubbing · {formatCurrency(tier.monthlyCost)}/mo · {formatCurrency(tier.setupCost)} setup
                        </p>
                      </div>
                      <button
                        onClick={() => subscribeNawas(currentLocation, tier.id)}
                        disabled={money < tier.setupCost}
                        className="px-3 py-2 rounded text-sm font-medium bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
                      >
                        Subscribe
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default MmrModal;
